import InlineSVG from "react-inlinesvg";
import styled, { keyframes } from "styled-components/macro";
import { Spinner } from "src/sudokit/components/Spinner";
import { useProjectStore } from "../../../stores/projectStore";

const indeterminate = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(300%); }
`;

const Container = styled.div`
  position: fixed;
  z-index: 10050;
  bottom: ${({ $bottomOffset }) => $bottomOffset}px;
  max-width: min(380px, calc(100vw - 32px));
  background: var(--menu-color);
  border: 1px solid color-mix(in srgb, var(--grey-3) 55%, transparent);
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px 14px;

  .header { display: flex; align-items: center; gap: 10px; }
  .icon { display: flex; width: 20px; height: 20px; }
  .icon svg path { stroke: none; fill: var(--grey-1); }
  .title { color: var(--grey-1); flex: 1; font-size: 15px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; }
  .status { color: var(--grey-2); font-size: 14px; line-height: 140%; margin: 0; }
  .track { background: color-mix(in srgb, var(--grey-3) 40%, transparent); border-radius: 999px; height: 6px; overflow: hidden; }
  .fill { background: var(--primary-cta); height: 100%; transition: width 300ms ease; }
  .fill.indeterminate { animation: ${indeterminate} 1.1s ease-in-out infinite; width: 33%; }
  .footnote { color: var(--grey-2); font-size: 12px; font-style: italic; margin: 0; }
  .content { border-top: 1px solid color-mix(in srgb, var(--grey-3) 35%, transparent); padding-top: 10px; }
`;

const DismissButton = styled.button`
  &&& {
    appearance: none;
    align-items: center;
    background: transparent !important;
    border: 0;
    border-radius: 0 !important;
    box-shadow: none;
    color: var(--grey-2) !important;
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 24px;
    font-family: inherit;
    font-size: 20px;
    font-weight: 400;
    height: 24px;
    justify-content: center;
    line-height: 1;
    margin: 0;
    padding: 0;
    width: 24px;
  }

  &&&:hover {
    background: transparent !important;
    color: var(--grey-1) !important;
  }

  &&&:focus-visible {
    background: transparent !important;
    outline: 2px solid var(--primary-cta);
    outline-offset: 2px;
  }
`;

export default function JobProgressBanner({
  title,
  statusText,
  progress = null,
  footnote,
  isComplete = false,
  onDismiss,
  children,
  bottomOffset = 16,
}) {
  const document = useProjectStore((state) => state.document);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const right = (document ? columnWidths.right.current : 0) + 16;
  const determinate = typeof progress === "number";
  const width = determinate ? Math.round(Math.min(1, Math.max(0, progress)) * 100) : null;

  return (
    <Container style={{ right }} $bottomOffset={bottomOffset} role="status" aria-live="polite">
      <div className="header">
        <span className="icon" aria-hidden="true"><InlineSVG src="/assets/editor-toolbar/collapse-empty-paragraphs.svg" /></span>
        <span className="title">{title}</span>
        {!isComplete ? <Spinner size="sm" /> : null}
        {onDismiss ? <DismissButton type="button" aria-label="Dismiss" onClick={onDismiss}>×</DismissButton> : null}
      </div>
      <p className="status">{statusText}</p>
      <div className="track" role="progressbar" aria-valuemin={0} aria-valuemax={100} {...(determinate ? { "aria-valuenow": width } : {})}>
        <div className={`fill${determinate ? "" : " indeterminate"}`} style={determinate ? { width: `${width}%` } : undefined} />
      </div>
      {footnote ? <p className="footnote">{footnote}</p> : null}
      {children ? <div className="content">{children}</div> : null}
    </Container>
  );
}
